import { Upload, UploadOnAddEvent } from '@progress/kendo-react-upload';
import { createRef, DragEvent, FC } from 'react';
import CrossIcon from 'src/assets/icons/CrossIcon';
import ExcelFileIcon from 'src/assets/icons/ExcelFileIcon';
import TextFileIcon from 'src/assets/icons/TextFileIcon';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import usePeriodView from 'src/hooks/usePeriodView';
import { SetState } from 'src/types/generic';
import { preventDefault } from 'src/utils';
import TickIcon from '../../../assets/icons/TickIcon';
import { useFileProps } from './Import';

interface UploadImportFileProps {
  file: File | null;
  setFile: SetState<File | null>;
}

const UploadImportFile: FC<UploadImportFileProps> = ({ file, setFile }) => {
  const { t } = usePeriodView();

  const uploadRef = createRef<Upload>();

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles.length > 0 && uploadRef && uploadRef.current) setFile(droppedFiles[0]);
  };

  const handleUpload = (e: UploadOnAddEvent) => {
    if (e.affectedFiles?.[0].getRawFile) setFile(e.affectedFiles?.[0]?.getRawFile());
  };

  const handleClick = () => {
    if (uploadRef) uploadRef?.current?.actionElement?.click();
  };

  const { isAcceptable, errors, info } = useFileProps(file);

  return (
    <div className="mb-2">
      <p className="font-bold text-xs mb-2">{t('Upload File')}</p>
      {file ? (
        <div className="flex border p-1 border-black bg-white justify-between items-center">
          <div className="flex items-center gap-1">
            <PopUp
              popupAlign={{ horizontal: 'left', vertical: 'bottom' }}
              anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
              style={{ marginTop: '-20px' }}
              content={
                <PopUpContentWrapper
                  type={isAcceptable ? 'Information' : 'Error'}
                  contents={isAcceptable ? info : errors}
                />
              }
            >
              {isAcceptable ? <TickIcon /> : <CrossIcon />}
            </PopUp>
            <p className="text-sm max-w-[250px] text-ellipsis">{file.name} </p>
          </div>
          <Button size="sm" color="error" onClick={() => setFile(null)}>
            Remove
          </Button>
        </div>
      ) : (
        <div
          className="h-[70px] bg-gray-300 flex items-center justify-center gap-3"
          onDrop={handleDrop}
          onDragOver={preventDefault}
          onClick={handleClick}
        >
          <div className="flex gap-2">
            <ExcelFileIcon width="24" height="24" />
            <TextFileIcon width="24" height="24" />
          </div>
          <div className="flex-col text-xs">
            <p className="font-bold">Drag and Drop</p>
            <p className="ml-2 text-gray-300">
              or
              <span className="text-[#3FA9DB] cursor-pointer ml-1">Browse</span>
            </p>
          </div>
        </div>
      )}
      <Upload
        onAdd={handleUpload}
        ref={uploadRef}
        accept={'.xls, .xlsx ,.txt'}
        className="k-dropzone-hint !hidden"
        autoUpload={false}
      />
    </div>
  );
};

export default UploadImportFile;
